import React, { useState, useEffect } from "react";
import { bulkUpdate, getAllTimes, deletePrayerTime } from "../api";
import { format12h } from "../utils/date";
import { useNavigate } from "react-router-dom";

export default function Admin() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");

  const [dbRows, setDbRows] = useState([]);

  // ⭐ Multi-delete
  const [selected, setSelected] = useState({});
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    loadDbView();
  }, []);

  async function loadDbView() {
    try {
      const data = await getAllTimes();
      setDbRows(data || []);
      setSelected({});
      setSelectAll(false);
    } catch (e) {
      console.error("Failed to load DB view", e);
      setStatus("Failed to load database view");
    }
  }

  // ⭐ SINGLE DELETE
  async function remove(id, date, day) {
    const ok = window.confirm(`Delete ${day} — ${date}?`);
    if (!ok) return;

    try {
      setStatus("Deleting...");
      await deletePrayerTime(id);

      setDbRows((prev) => prev.filter((r) => (r._id || r.id) !== id));
      setStatus("Deleted successfully ✔");
    } catch (e) {
      console.error("Delete failed", e);
      setStatus("Delete failed: " + (e.message || "Unknown error"));
    }
  }

  // ⭐ MULTI DELETE
  async function removeSelected() {
    const ids = Object.keys(selected).filter((id) => selected[id]);

    if (ids.length === 0) {
      alert("No rows selected");
      return;
    }

    const ok = window.confirm(`Delete ${ids.length} selected rows?`);
    if (!ok) return;

    try {
      setStatus("Deleting selected rows...");

      for (const id of ids) {
        await deletePrayerTime(id);
      }

      setDbRows((prev) => prev.filter((r) => !selected[r._id || r.id]));
      setSelected({});
      setSelectAll(false);

      setStatus("Selected rows deleted ✔");
    } catch (e) {
      console.error("Bulk delete failed", e);
      setStatus("Bulk delete failed: " + e.message);
    }
  }

  // ⭐ UPDATE A FIELD IN DB TABLE (NOW EDITABLE)
  function updateDbField(id, field, value) {
    setDbRows((prev) =>
      prev.map((r) => ((r._id || r.id) === id ? { ...r, [field]: value } : r)),
    );
  }

  // ⭐ UPDATE DATE → AUTO UPDATE DAY
  function updateDbDate(id, newDate) {
    setDbRows((prev) =>
      prev.map((r) => {
        if ((r._id || r.id) !== id) return r;

        const [mm, dd, yy] = newDate.split("/").map(Number);
        const d = new Date(2000 + yy, mm - 1, dd);

        return {
          ...r,
          date: newDate,
          day: !isNaN(d.getTime())
            ? d.toLocaleDateString("en-US", { weekday: "long" })
            : r.day,
        };
      }),
    );
  }

  // ⭐ SAVE TO DATABASE (WITH ALERT)
  async function onSave() {
    setStatus("Updating...");

    try {
      await bulkUpdate(dbRows);

      alert("Successfully uploaded to database");

      setStatus("Updated successfully ✔");
      await loadDbView();
    } catch (e) {
      setStatus("Update failed: " + e.message);
    }
  }

  function onLogout() {
    localStorage.removeItem("token");
    navigate("/login");
  }

  return (
    <div style={{ padding: "20px" }}>
      <h1 style={{ textAlign: "center" }}>View Database & Remove Data</h1>

      {/* ⭐ DELETE SELECTED BUTTON */}
      <div style={{ textAlign: "center", marginBottom: "10px" }}>
        <button
          onClick={removeSelected}
          style={{
            padding: "8px 20px",
            background: "red",
            color: "white",
            borderRadius: "6px",
          }}
        >
          Delete Selected
        </button>
      </div>

      {/* ⭐ DATABASE TABLE */}
      <div
        style={{
          maxHeight: "500px",
          overflowY: "auto",
          border: "1px solid #ccc",
          padding: "10px",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr
              style={{
                background: "#ddd",
                position: "sticky",
                top: 0,
                zIndex: 10,
              }}
            >
              <th>
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSelectAll(checked);

                    const newSelected = {};
                    dbRows.forEach((r) => {
                      const id = r._id || r.id;
                      newSelected[id] = checked;
                    });
                    setSelected(newSelected);
                  }}
                />
              </th>

              <th>Date</th>
              <th>Day</th>
              <th>Fajr</th>
              <th>Fajr Iqamah</th>
              <th>Dhuhr</th>
              <th>Dhuhr Iqamah</th>
              <th>Asr</th>
              <th>Asr Iqamah</th>
              <th>Maghrib</th>
              <th>Maghrib Iqamah</th>
              <th>Isha</th>
              <th>Isha Iqamah</th>
              <th>Jumuah</th>
              <th>Jumuah Iqamah</th>
              <th>Remove</th>
            </tr>
          </thead>

          <tbody>
            {dbRows.map((r) => {
              const id = r._id || r.id;

              return (
                <tr key={id} style={{ borderBottom: "1px solid #ccc" }}>
                  <td>
                    <input
                      type="checkbox"
                      checked={!!selected[id]}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSelected((prev) => ({
                          ...prev,
                          [id]: checked,
                        }));
                      }}
                    />
                  </td>

                  {/* ⭐ Editable Date */}
                  <td>
                    <input
                      type="text"
                      value={r.date}
                      onChange={(e) => updateDbDate(id, e.target.value)}
                      style={{ width: "80px", textAlign: "center" }}
                    />
                  </td>

                  {/* ⭐ Auto-updated Day */}
                  <td>{r.day}</td>

                  {/* ⭐ Editable Times */}
                  {[
                    "fajr",
                    "fajrIqamah",
                    "dhuhr",
                    "dhuhrIqamah",
                    "asr",
                    "asrIqamah",
                    "maghrib",
                    "maghribIqamah",
                    "isha",
                    "ishaIqamah",
                    "jumuah",
                    "jumuahIqamah",
                  ].map((field) => (
                    <td key={field}>
                      <input
                        type="time"
                        value={r[field]}
                        onChange={(e) =>
                          updateDbField(id, field, e.target.value)
                        }
                      />
                    </td>
                  ))}

                  <td>
                    <button
                      style={{
                        color: "black",
                        padding: "1px 15px",
                        cursor: "pointer",
                        fontSize: "20px",
                      }}
                      onClick={() => remove(id, r.date, r.day)}
                    >
                      ❌
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ACTION BUTTONS */}
      <div style={{ marginTop: "20px", textAlign: "center" }}>
        <button onClick={onSave} style={{ padding: "10px 20px" }}>
          Update Database
        </button>
        <button
          onClick={onLogout}
          style={{ padding: "10px 20px", marginLeft: "20px" }}
        >
          Logout
        </button>
      </div>

      <div style={{ textAlign: "center", marginTop: "10px" }}>{status}</div>
    </div>
  );
}
